// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AuthProvider from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChakraProvider } from '@chakra-ui/react';
import './theme.css';

// Import M PLUS Rounded 1c font
import '@fontsource/m-plus-rounded-1c/400.css';


import HomePage from './pages/HomePage';
import DORsPage from './pages/DORsPage';
import TraineeDORsPage from './pages/TraineeDORsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './utils/ProtectedRoute';
import 'react-toastify/dist/ReactToastify.css';
import CreateDOR from './components/CreateDOR';
import DORDetailPage from './pages/DORDetailPage';
import AppContent from './components/AppContent';
import TraineeReportsPage from './pages/TraineeReportsPage';
import OfficerSafetyReportDetailPage from './pages/OfficerSafetyReportDetailPage';
import CreateReportSelection from './components/CreateReportSelection';
import CreateOfficerSafetyReport from './components/CreateOfficerSafetyReport';
import CreateEndOfWeekReport from './components/CreateEndOfWeekReport';
import EndOfWeekReportDetailPage from './pages/EndOfWeekReportDetailPage';
import IdleTimerContainer from './components/IdleTimerContainer';
import SearchReports from './components/SearchReports';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import DocumentationPage from './pages/DocumentationPage';
import NotFoundPage from './pages/NotFoundPage';
import ManageDuplicateDORs from './components/ManageDuplicateDORs';
import ManageDORs from './components/ManageDORs';


function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <ChakraProvider>
              <NotificationProvider>
                <IdleTimerContainer>
                  <Router>
                    <ScrollToTop />
                    <AppContent>
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/login" element={<LoginPage />} />

                        {/* Protected Routes */}
                        <Route
                          path="/"
                          element={(
                            <ProtectedRoute>
                              <HomePage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/dors"
                          element={(
                            <ProtectedRoute allowedRoles={['trainer', 'admin']}>
                              <DORsPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/dor/:dorId"
                          element={(
                            <ProtectedRoute>
                              <DORDetailPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/search-reports"
                          element={(
                            <ProtectedRoute allowedRoles={['trainee', 'trainer', 'admin']}>
                              <SearchReports />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/trainee-dors/:traineeId"
                          element={(
                            <ProtectedRoute allowedRoles={['trainer', 'admin']}>
                              <TraineeDORsPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/trainee-reports/:traineeId"
                          element={(
                            <ProtectedRoute allowedRoles={['trainer', 'admin']}>
                              <TraineeReportsPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/officersafetyreport/:reportId"
                          element={(
                            <ProtectedRoute allowedRoles={['trainee', 'trainer', 'admin']}>
                              <OfficerSafetyReportDetailPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/endofweekreport/:reportId"
                          element={(
                            <ProtectedRoute allowedRoles={['trainee', 'trainer', 'admin']}>
                              <EndOfWeekReportDetailPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/create-report"
                          element={(
                            <ProtectedRoute allowedRoles={['trainer', 'admin']}>
                              <CreateReportSelection />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/create-dor"
                          element={(
                            <ProtectedRoute allowedRoles={['trainer', 'admin']}>
                              <CreateDOR />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/create-officer-safety-report"
                          element={(
                            <ProtectedRoute allowedRoles={['trainer', 'admin']}>
                              <CreateOfficerSafetyReport />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/create-end-of-week-report"
                          element={(
                            <ProtectedRoute allowedRoles={['trainer', 'admin']}>
                              <CreateEndOfWeekReport />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/profile"
                          element={(
                            <ProtectedRoute>
                              <ProfilePage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/settings"
                          element={(
                            <ProtectedRoute allowedRoles={['admin']}>
                              <SettingsPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/docs"
                          element={(
                            <ProtectedRoute allowedRoles={['admin']}>
                              <DocumentationPage />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/manage-duplicates"
                          element={(
                            <ProtectedRoute allowedRoles={['admin']}>
                              <ManageDuplicateDORs />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/manage-dors"
                          element={(
                            <ProtectedRoute allowedRoles={['admin']}>
                              <ManageDORs />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/privacy-policy"
                          element={(
                            <ProtectedRoute>
                              <PrivacyPolicy />
                            </ProtectedRoute>
                          )}
                        />
                        <Route
                          path="/terms-of-service"
                          element={(
                            <ProtectedRoute>
                              <TermsOfService />
                            </ProtectedRoute>
                          )}
                        />

                        {/* Catch-all Route */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </AppContent>
                  </Router>
                </IdleTimerContainer>
              </NotificationProvider>
            </ChakraProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
